import React, { useEffect, useRef } from 'react';

/**
 * Mancha de luz muy tenue que sigue al puntero dentro de su sección.
 *
 * No se percibe conscientemente. Lo que hace es que una superficie navy grande
 * y plana deje de sentirse como un PDF: la luz se mueve, así que el plano
 * parece tener material. Es uno de los recursos que más devuelven por línea de
 * código en los sitios de estudio.
 *
 * Tres decisiones para que salga gratis en rendimiento:
 *
 * - Las coordenadas viajan como VARIABLES CSS. El navegador solo repinta un
 *   degradado; no hay transform encadenado ni cálculo de layout.
 * - Se escribe dentro de `requestAnimationFrame`. `pointermove` dispara docenas
 *   de veces por fotograma y escribir estilo en cada uno es tirar trabajo.
 * - Escucha en el ELEMENTO PADRE, no en `window`: fuera de la sección no hay
 *   nada que actualizar.
 *
 * Es decoración pura, así que va `aria-hidden` y sin eventos de puntero. En
 * pantallas táctiles el CSS lo oculta: sin cursor que seguir, la mancha se
 * quedaría clavada donde cayó el último toque.
 */
const Resplandor: React.FC = () => {
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = ref.current;
        const seccion = el?.parentElement;
        if (!el || !seccion) return;

        /* No se consulta `prefers-reduced-motion`: esta luz no se mueve sola,
           la mueve la mano del usuario. Nada ocurre si nadie toca el ratón, y
           eso es justo lo contrario de lo que esa preferencia pide evitar. */
        if (!window.matchMedia('(hover: hover)').matches) return;

        let pendiente = false;
        let x = 0;
        let y = 0;

        const pintar = () => {
            pendiente = false;
            el.style.setProperty('--rp-x', `${x}px`);
            el.style.setProperty('--rp-y', `${y}px`);
        };

        const alMover = (e: PointerEvent) => {
            const r = seccion.getBoundingClientRect();
            x = e.clientX - r.left;
            y = e.clientY - r.top;
            if (pendiente) return;
            pendiente = true;
            requestAnimationFrame(pintar);
        };

        const entrar = () => el.setAttribute('data-activo', '');
        const salir = () => el.removeAttribute('data-activo');

        seccion.addEventListener('pointermove', alMover);
        seccion.addEventListener('pointerenter', entrar);
        seccion.addEventListener('pointerleave', salir);
        return () => {
            seccion.removeEventListener('pointermove', alMover);
            seccion.removeEventListener('pointerenter', entrar);
            seccion.removeEventListener('pointerleave', salir);
        };
    }, []);

    return <div ref={ref} className="rp-resplandor" aria-hidden="true" />;
};

export default Resplandor;
